
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import BookingCalendar from "@/components/BookingCalendar";
import WaitlistForm from "@/components/WaitlistForm";
import PromoCodeInput from "./PromoCodeInput";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { PromoCode } from "@/types/supabase";
import QuoteEmailDialog from "./QuoteEmailDialog";
import CancellationPolicyBox from "./CancellationPolicyBox";
import { Minus, Plus } from "lucide-react";

interface BookingSummaryProps {
  bookingDetails: {
    checkIn: Date;
    checkOut: Date;
    nights: number;
    subtotal: number;
    cleaningFee: number;
    touristTax: number;
    total: number;
    discount: number;
    guestsCount?: number;
  } | null;
  showWaitlistDialog: boolean;
  setShowWaitlistDialog: (show: boolean) => void;
  checkIn?: Date;
  checkOut?: Date;
  onDatesSelected: (dates: { checkIn: Date | undefined; checkOut: Date | undefined }) => void;
  appliedPromoCode: PromoCode | null;
  onApplyPromoCode: (code: string) => void;
  guestsCount?: number;
  onGuestsCountChange?: (count: number) => void;
  isValidatingPromo?: boolean;
}

const BookingSummary = ({
  bookingDetails,
  showWaitlistDialog,
  setShowWaitlistDialog,
  checkIn,
  checkOut,
  onDatesSelected,
  appliedPromoCode,
  onApplyPromoCode,
  guestsCount = 2,
  onGuestsCountChange,
  isValidatingPromo = false,
}: BookingSummaryProps) => {
  const [showQuoteForm, setShowQuoteForm] = useState(false);

  const handleGuestsIncrease = () => {
    if (onGuestsCountChange && guestsCount < 10) {
      onGuestsCountChange(guestsCount + 1);
    }
  };

  const handleGuestsDecrease = () => {
    if (onGuestsCountChange && guestsCount > 1) {
      onGuestsCountChange(guestsCount - 1);
    }
  };

  const handleGuestsInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (onGuestsCountChange && !isNaN(value) && value >= 1 && value <= 10) {
      onGuestsCountChange(value);
    }
  };

  return (
    <Card className="shadow-md sticky top-24">
      <CardContent className="p-6">
        <h2 className="text-2xl font-playfair text-villa-blue mb-6">
          Résumé de votre séjour
        </h2>
        {bookingDetails ? (
          <>
            <div className="space-y-4 mb-6">
              <div className="flex justify-between items-center pb-2 border-b">
                <span className="font-medium">Dates</span>
                <span className="text-sm sm:text-base">
                  {format(bookingDetails.checkIn, "PP", { locale: fr })} → {format(bookingDetails.checkOut, "PP", { locale: fr })}
                </span>
              </div>
              {onGuestsCountChange && (
                <div className="flex justify-between items-center pb-2 border-b">
                  <span className="font-medium">Voyageurs</span>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleGuestsDecrease}
                      disabled={guestsCount <= 1}
                      className="h-8 w-8 p-0"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <Input 
                      type="number" 
                      min={1}
                      max={10}
                      value={guestsCount}
                      onChange={handleGuestsInputChange}
                      className="w-16 text-center h-8 text-sm" 
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleGuestsIncrease}
                      disabled={guestsCount >= 10}
                      className="h-8 w-8 p-0"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              <div className="flex justify-between items-center pb-2 border-b">
                <span className="font-medium">Durée</span>
                <span>{bookingDetails.nights} nuits</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b">
                <span className="font-medium">Sous-total</span>
                <span>{bookingDetails.subtotal.toFixed(2)} €</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b">
                <span className="font-medium">Frais de ménage</span>
                <span>{bookingDetails.cleaningFee.toFixed(2)} €</span>
              </div>
              {bookingDetails.touristTax > 0 && (
                <div className="flex justify-between items-center pb-2 border-b">
                  <span className="font-medium">Taxe de séjour</span>
                  <span>{bookingDetails.touristTax.toFixed(2)} €</span>
                </div>
              )}
              <PromoCodeInput
                onApply={onApplyPromoCode}
                appliedCode={appliedPromoCode}
                isValidating={isValidatingPromo}
              />
              {bookingDetails.discount > 0 && (
                <div className="flex justify-between items-center text-green-600">
                  <span className="font-medium">Réduction</span>
                  <span>-{bookingDetails.discount.toFixed(2)} €</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2 text-lg font-bold">
                <span>Total</span>
                <span>{bookingDetails.total.toFixed(2)} €</span>
              </div>
            </div>
            <div className="mt-4 mb-6">
              <Button
                variant="outline"
                className="w-full border-villa-turquoise text-villa-blue hover:bg-villa-turquoise hover:text-white"
                onClick={() => setShowQuoteForm(true)}
              >
                Recevoir ce devis par email
              </Button>
              <QuoteEmailDialog
                open={showQuoteForm}
                onOpenChange={setShowQuoteForm}
                bookingDetails={bookingDetails}
                guestsCount={guestsCount}
                appliedPromoCode={appliedPromoCode}
              />
            </div>
            <CancellationPolicyBox />
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">
              Veuillez sélectionner vos dates pour voir le détail de votre séjour
            </p>
            <Dialog>
              <DialogTrigger asChild>
                <Button className="bg-villa-turquoise hover:bg-opacity-90">
                  Sélectionner des dates
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Calendrier de disponibilité</DialogTitle>
                  <DialogDescription>
                    Sélectionnez vos dates d'arrivée et de départ
                  </DialogDescription>
                </DialogHeader>
                <BookingCalendar onDatesSelected={onDatesSelected} />
              </DialogContent>
            </Dialog>
          </div>
        )}
        <div className="mt-6">
          <h3 className="text-lg font-medium mb-2">Dates indisponibles ?</h3>
          <Button
            variant="outline"
            className="w-full border-villa-turquoise text-villa-blue hover:bg-villa-turquoise hover:text-white"
            onClick={() => setShowWaitlistDialog(true)}
          >
            Rejoindre la liste d'attente
          </Button>
        </div>
      </CardContent>
      <Dialog open={showWaitlistDialog} onOpenChange={setShowWaitlistDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Rejoindre la liste d'attente</DialogTitle>
            <DialogDescription>
              Laissez-nous vos coordonnées et nous vous contacterons en priorité si des dates se libèrent.
            </DialogDescription>
          </DialogHeader>
          <WaitlistForm checkIn={checkIn} checkOut={checkOut} />
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default BookingSummary;
